import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdSecurity } from 'react-icons/md';
import '../master/MasterData.css';

interface User {
  userId: number;
  username: string;
  fullName: string;
  email: string;
  role: 'ADMIN' | 'MANAGER' | 'ACCOUNTANT' | 'STAFF' | 'VIEWER';
  department: string;
  status: 'ACTIVE' | 'INACTIVE';
  lastLogin: string;
  permissions: string[];
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    // Sample data - would be fetched from API
    setUsers([
      {
        userId: 1,
        username: 'admin',
        fullName: 'System Administrator',
        email: 'admin@dewfoam.com',
        role: 'ADMIN',
        department: 'IT',
        status: 'ACTIVE',
        lastLogin: '2025-03-31 14:30:00',
        permissions: ['*']
      },
      {
        userId: 2,
        username: 'finance.manager',
        fullName: 'Maria Santos',
        email: 'maria.santos@dewfoam.com',
        role: 'MANAGER',
        department: 'Finance',
        status: 'ACTIVE',
        lastLogin: '2025-03-31 10:15:00',
        permissions: ['sales', 'purchases', 'gl', 'payroll', 'reports']
      },
      {
        userId: 3,
        username: 'accountant1',
        fullName: 'Juan Dela Cruz',
        email: 'juan.delacruz@dewfoam.com',
        role: 'ACCOUNTANT',
        department: 'Finance',
        status: 'ACTIVE',
        lastLogin: '2025-03-31 09:00:00',
        permissions: ['sales', 'purchases', 'gl', 'reports']
      },
      {
        userId: 4,
        username: 'hr.staff',
        fullName: 'Ana Garcia',
        email: 'ana.garcia@dewfoam.com',
        role: 'STAFF',
        department: 'HR',
        status: 'ACTIVE',
        lastLogin: '2025-03-30 16:45:00',
        permissions: ['payroll']
      },
      {
        userId: 5,
        username: 'warehouse.staff',
        fullName: 'Pedro Reyes',
        email: 'pedro.reyes@dewfoam.com',
        role: 'STAFF',
        department: 'Warehouse',
        status: 'ACTIVE',
        lastLogin: '2025-03-31 08:30:00',
        permissions: ['inventory']
      },
      {
        userId: 6,
        username: 'sales.viewer',
        fullName: 'Roberto Tan',
        email: 'roberto.tan@dewfoam.com',
        role: 'VIEWER',
        department: 'Sales',
        status: 'ACTIVE',
        lastLogin: '2025-03-29 11:20:00',
        permissions: ['reports']
      },
      {
        userId: 7,
        username: 'former.employee',
        fullName: 'Former Employee',
        email: 'former@dewfoam.com',
        role: 'STAFF',
        department: 'Finance',
        status: 'INACTIVE',
        lastLogin: '2025-02-15 17:00:00',
        permissions: []
      }
    ]);
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterRole === 'ALL' || user.role === filterRole;
    return matchesSearch && matchesFilter;
  });

  const handleAdd = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.userId !== userId));
    }
  };

  const handleManagePermissions = (user: User) => {
    setSelectedUser(user);
    setShowPermissionModal(true);
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'badge-danger';
      case 'MANAGER': return 'badge-primary';
      case 'ACCOUNTANT': return 'badge-info';
      case 'STAFF': return 'badge-success';
      case 'VIEWER': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    return status === 'ACTIVE' ? 'badge-success' : 'badge-secondary';
  };

  const allModules = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'master', name: 'Master Data' },
    { id: 'sales', name: 'Sales / AR' },
    { id: 'purchases', name: 'Purchases / AP' },
    { id: 'inventory', name: 'Inventory' },
    { id: 'gl', name: 'General Ledger' },
    { id: 'payroll', name: 'Payroll' },
    { id: 'reports', name: 'Reports' },
    { id: 'settings', name: 'Settings' }
  ];

  const summary = {
    total: users.length,
    active: users.filter(u => u.status === 'ACTIVE').length,
    inactive: users.filter(u => u.status === 'INACTIVE').length,
    admins: users.filter(u => u.role === 'ADMIN').length
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>User Management</h2>
            <p className="subtitle">Manage user accounts and access permissions</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Add User
          </button>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">👥</div>
            <div className="summary-content">
              <p className="summary-label">Total Users</p>
              <p className="summary-value">{summary.total}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">✅</div>
            <div className="summary-content">
              <p className="summary-label">Active Users</p>
              <p className="summary-value" style={{ color: '#28a745' }}>{summary.active}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">⛔</div>
            <div className="summary-content">
              <p className="summary-label">Inactive Users</p>
              <p className="summary-value" style={{ color: '#6c757d' }}>{summary.inactive}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🔐</div>
            <div className="summary-content">
              <p className="summary-label">Administrators</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>{summary.admins}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filterRole === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterRole('ALL')}
              >
                All Roles
              </button>
              <button
                className={`filter-tab ${filterRole === 'ADMIN' ? 'active' : ''}`}
                onClick={() => setFilterRole('ADMIN')}
              >
                Admin
              </button>
              <button
                className={`filter-tab ${filterRole === 'MANAGER' ? 'active' : ''}`}
                onClick={() => setFilterRole('MANAGER')}
              >
                Manager
              </button>
              <button
                className={`filter-tab ${filterRole === 'ACCOUNTANT' ? 'active' : ''}`}
                onClick={() => setFilterRole('ACCOUNTANT')}
              >
                Accountant
              </button>
              <button
                className={`filter-tab ${filterRole === 'STAFF' ? 'active' : ''}`}
                onClick={() => setFilterRole('STAFF')}
              >
                Staff
              </button>
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.userId}>
                    <td className="code">{user.username}</td>
                    <td className="name">{user.fullName}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{user.department}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{new Date(user.lastLogin).toLocaleString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(user)}
                          title="Edit User"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleManagePermissions(user)}
                          title="Manage Permissions"
                          style={{ color: '#1a237e' }}
                        >
                          <MdSecurity />
                        </button>
                        {user.role !== 'ADMIN' && (
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(user.userId)}
                            title="Delete User"
                          >
                            <MdDelete />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="no-data">
                <p>No users found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Username *</label>
                <input type="text" placeholder="Enter username" defaultValue={editingUser?.username} />
              </div>
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" placeholder="Enter full name" defaultValue={editingUser?.fullName} />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" placeholder="Enter email" defaultValue={editingUser?.email} />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select defaultValue={editingUser?.role || 'STAFF'}>
                  <option value="ADMIN">Admin</option>
                  <option value="MANAGER">Manager</option>
                  <option value="ACCOUNTANT">Accountant</option>
                  <option value="STAFF">Staff</option>
                  <option value="VIEWER">Viewer</option>
                </select>
              </div>
              <div className="form-group">
                <label>Department *</label>
                <input type="text" placeholder="Enter department" defaultValue={editingUser?.department} />
              </div>
              <div className="form-group">
                <label>Password {!editingUser && '*'}</label>
                <input type="password" placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"} />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select defaultValue={editingUser?.status || 'ACTIVE'}>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPermissionModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Permissions - {selectedUser.fullName}</h3>
              <button className="close-btn" onClick={() => setShowPermissionModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <strong>Role:</strong> <span className={`badge ${getRoleBadgeClass(selectedUser.role)}`}>{selectedUser.role}</span>
              </div>

              <h4 style={{ marginBottom: '15px', color: '#1a237e' }}>Module Access Permissions</h4>

              {selectedUser.permissions.includes('*') ? (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#ffebee',
                  borderRadius: '8px',
                  border: '1px solid #ef5350',
                  marginBottom: '20px'
                }}>
                  <strong style={{ color: '#dc3545' }}>⚠️ Administrator Access</strong>
                  <p style={{ margin: '10px 0 0 0', color: '#666' }}>
                    This user has full access to all modules and settings.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  {allModules.map(module => (
                    <label key={module.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: selectedUser.permissions.includes(module.id) ? '#e3f2fd' : '#fff'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedUser.permissions.includes(module.id)}
                        style={{ marginRight: '10px', width: '18px', height: '18px' }}
                        readOnly
                      />
                      <span style={{ fontWeight: selectedUser.permissions.includes(module.id) ? 'bold' : 'normal' }}>
                        {module.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#1a237e' }}>Permission Summary</h5>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  {selectedUser.permissions.includes('*')
                    ? 'Full system access granted'
                    : `Access granted to ${selectedUser.permissions.length} module(s)`
                  }
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPermissionModal(false)}>
                Close
              </button>
              <button type="button" className="btn btn-primary">
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default UserManagement;
